import { useState } from "react";
import { useEffect } from "react";
import { Loader2, Send } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PACKAGES, packageLabel } from "@/components/Packages";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";

const CANDLES = "https://images.unsplash.com/photo-1636226942649-ee15d2a7ce04?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwyfHxnbG93aW5nJTIwb2lsJTIwbGFtcCUyMGRhcmslMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc4NjAwMDA3Mnww&ixlib=rb-4.1.0&q=70&w=1400&auto=format";

const EVENT_TYPES = ["Wedding", "Private Satsang", "Corporate Evening", "College Festival", "Temple / Community Event", "Other"];

const PACKAGE_OPTIONS = ["Not sure yet — advise us", ...PACKAGES.map(packageLabel)];

const inputCls = "w-full bg-ink/80 border border-bone/15 px-4 py-3.5 text-bone placeholder:text-ash/60 focus:border-saffron focus:outline-none transition-colors";

export default function Enquiry({ preselectedPackage = "", preselectedCity = "" }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", event_type: EVENT_TYPES[0], event_date: "", city: "", package: PACKAGE_OPTIONS[0], message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    if (preselectedPackage) setForm((f) => ({ ...f, package: preselectedPackage }));
  }, [preselectedPackage]);

  useEffect(() => {
    if (preselectedCity) setForm((f) => ({ ...f, city: preselectedCity }));
  }, [preselectedCity]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Enquiry sent — we'll reach out within a day.");
      setForm({ name: "", email: "", phone: "", event_type: EVENT_TYPES[0], event_date: "", city: "", package: PACKAGE_OPTIONS[0], message: "" });
    } catch (err) {
      toast.error(apiError(err, "Could not send enquiry"));
    }
    setSending(false);
  };

  return (
    <section id="enquire" className="relative px-6 md:px-12 py-24 md:py-32 overflow-hidden" data-testid="enquiry-section">
      <img src={CANDLES} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-[0.08]" />
      <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-16">
        <div>
          <SectionHeading overline="Private Shows" title="Bring Sumiran" italic="to your evening" testid="enquiry-heading" />
          <Reveal delay={0.1}>
            <p className="mt-8 text-base lg:text-lg leading-relaxed text-bone/70 max-w-md">
              Weddings, anniversaries, corporate retreats, temple functions — tell us about your
              gathering and we will craft a devotional set for it. Every enquiry reaches the band directly.
            </p>
            <div className="mt-10 space-y-4 border-l-2 border-saffron/50 pl-6">
              <p className="text-sm text-ash">Typical set — 2.5 to 3 hours</p>
              <p className="text-sm text-ash">Full ensemble with sound, or acoustic satsang format</p>
              <p className="text-sm text-ash">Pan-India travel</p>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.15}>
          <form onSubmit={submit} className="border border-bone/15 bg-surface/70 backdrop-blur-sm p-8 md:p-10 space-y-4" data-testid="enquiry-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input required placeholder="Your name" value={form.name} onChange={set("name")} className={inputCls} data-testid="enquiry-name-input" />
              <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} className={inputCls} data-testid="enquiry-email-input" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input required placeholder="Phone" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="enquiry-phone-input" />
              <select value={form.event_type} onChange={set("event_type")} className={inputCls} data-testid="enquiry-type-select">
                {EVENT_TYPES.map((t) => <option key={t} value={t} className="bg-ink">{t}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input type="date" value={form.event_date} onChange={set("event_date")} className={inputCls} data-testid="enquiry-date-input" />
              <input placeholder="City" value={form.city} onChange={set("city")} className={inputCls} data-testid="enquiry-city-input" />
            </div>
            <select value={form.package} onChange={set("package")} className={inputCls} data-testid="enquiry-package-select">
              {PACKAGE_OPTIONS.map((p) => <option key={p} value={p} className="bg-ink">{p}</option>)}
            </select>
            <textarea rows={4} placeholder="Tell us about your event…" value={form.message} onChange={set("message")} className={inputCls} data-testid="enquiry-message-input" />
            <button type="submit" disabled={sending}
              className="w-full py-4 bg-saffron text-ink font-medium tracking-wide hover:bg-gold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="enquiry-submit-btn">
              {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={16} />}
              {sending ? "Sending…" : "Send Enquiry"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
